import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
    PlayCircleIcon,
    SparkleIcon,
    LightningIcon,
    HeartIcon,
    TrendUpIcon,
    GlobeIcon,
    DownloadSimpleIcon,
    ArrowRightIcon,
    AndroidLogoIcon,
    AppleLogoIcon,
    StarIcon,
    ShieldCheckIcon,
    UsersThreeIcon,
    PaintBrushIcon,
    MusicNotesIcon,
    ChatCenteredDotsIcon,
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import FAQItem from "@/components/FAQItem";

const FEATURES = [
    {
        icon: PlayCircleIcon,
        title: "Endless Reels Feed",
        body: "Vertical scroll-snap reels designed for one-thumb scrolling. Bollywood beats, cricket nostalgia, street-food vlogs — all curated for India.",
        color: "#E01A4F",
    },
    {
        icon: SparkleIcon,
        title: "Made in India",
        body: "Local creators, regional languages, desi humour. From Tier-1 metros to Tier-3 towns — every Indian vibe lives here.",
        color: "#FFA700",
    },
    {
        icon: LightningIcon,
        title: "Blazing Fast",
        body: "Optimised for 2G to 5G. Reels load before you can blink. Even on low-end devices, smooth playback guaranteed.",
        color: "#E01A4F",
    },
    {
        icon: HeartIcon,
        title: "Creator First",
        body: "Zero gatekeeping. Upload free, build audience, earn through India Reels Creator Fund and brand collaborations.",
        color: "#FFA700",
    },
    {
        icon: TrendUpIcon,
        title: "Smart Trending",
        body: "Algorithm built for India — knows whether you love Punjabi rap or Carnatic remixes. Personalisation that actually understands you.",
        color: "#E01A4F",
    },
    {
        icon: GlobeIcon,
        title: "13+ Languages",
        body: "Hindi, Tamil, Telugu, Bangla, Marathi, Gujarati, Kannada, Punjabi, Malayalam, Odia, Assamese, Bhojpuri & more.",
        color: "#FFA700",
    },
];

const STATS = [
    { value: "50K+", label: "Creators" },
    { value: "2M+", label: "Reels Watched" },
    { value: "13+", label: "Languages" },
    { value: "100%", label: "Made in India" },
];

const SCREENSHOTS = [
    {
        url: "https://images.pexels.com/photos/7348139/pexels-photo-7348139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=600",
        caption: "@priyasharma",
        subtitle: "Diwali ki tayariyaan 💛",
    },
    {
        url: "https://images.pexels.com/photos/35565046/pexels-photo-35565046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=600",
        caption: "@neha.k",
        subtitle: "Mumbai nights hit different ✨",
    },
    {
        url: "https://images.pexels.com/photos/34014527/pexels-photo-34014527.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=600",
        caption: "@arjunmehra",
        subtitle: "5am runs in Delhi smog 🏃",
    },
    {
        url: "https://images.pexels.com/photos/9665193/pexels-photo-9665193.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=600",
        caption: "@rajasthanqueen",
        subtitle: "Marigold season in Jaipur 🌼",
    },
];

const CREATORS = [
    {
        url: "https://images.pexels.com/photos/7348139/pexels-photo-7348139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=300",
        handle: "@priyasharma",
        followers: "248K",
        niche: "Dance · Mumbai",
    },
    {
        url: "https://images.pexels.com/photos/34014527/pexels-photo-34014527.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=300",
        handle: "@arjunmehra",
        followers: "152K",
        niche: "Travel · Delhi",
    },
    {
        url: "https://images.pexels.com/photos/35565046/pexels-photo-35565046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=300",
        handle: "@neha.k",
        followers: "98K",
        niche: "Fashion · Bangalore",
    },
    {
        url: "https://images.pexels.com/photos/9665193/pexels-photo-9665193.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=300",
        handle: "@rajasthanqueen",
        followers: "210K",
        niche: "Travel · Jaipur",
    },
];

const FAQS = [
    {
        q: "Is India Reels really free to use?",
        a: "Yes — 100% free. Download, scroll, upload, comment, follow — sab kuch free. No premium tiers, no paywalls. Optional Creator Fund participation for eligible creators only.",
    },
    {
        q: "Which Indian languages are supported?",
        a: "We support 13+ Indian languages: Hindi, Tamil, Telugu, Bangla, Marathi, Gujarati, Kannada, Punjabi, Malayalam, Odia, Assamese, Bhojpuri and English. More regional languages are added based on demand.",
    },
    {
        q: "When does the app launch on Play Store and App Store?",
        a: "Android (Google Play) launches Q1 2026. iOS App Store launches Q2 2026. Join the waitlist on our homepage to get notified the moment we go live.",
    },
    {
        q: "How does the Creator Fund work?",
        a: "Eligible creators (10K+ followers, 100K+ monthly views, India residents 18+, KYC verified) earn monthly payouts via UPI based on views, engagement and brand-safe content. Full terms at /creator-fund (coming soon).",
    },
    {
        q: "Is my data safe on India Reels?",
        a: "Absolutely. Data is hosted in India, encrypted in transit (TLS) and at rest (AES-256). We never sell personal data to third parties. Read our complete Privacy Policy for details.",
    },
    {
        q: "Can I download my data or delete my account?",
        a: "Yes. You have full data portability and deletion rights. Visit /delete-account to submit a deletion request — we process it within 30 days.",
    },
    {
        q: "How is India Reels different from Instagram Reels or YouTube Shorts?",
        a: "We're built ground-up for India: regional languages first, lighter bandwidth optimised for 2G/3G, fair revenue share for creators, zero foreign algorithm bias, and India-hosted servers for faster loading and Indian data sovereignty.",
    },
    {
        q: "How do I report inappropriate content?",
        a: "Every reel has a Report option. Our moderation team (humans + AI) reviews reports within 24 hours. For urgent issues, email moderation@india-reels.com or use the in-app Support form.",
    },
];

function PhonePreview({ delay = 0 }) {
    const [idx, setIdx] = useState(0);
    const timerRef = useRef(null);
    useEffect(() => {
        timerRef.current = setInterval(() => {
            setIdx((i) => (i + 1) % SCREENSHOTS.length);
        }, 3500);
        return () => clearInterval(timerRef.current);
    }, []);

    return (
        <div className="relative mx-auto fade-up" style={{ animationDelay: `${delay}ms` }}>
            <div className="relative w-[260px] md:w-[280px] h-[520px] md:h-[560px] rounded-[44px] bg-gradient-to-br from-primary to-secondary p-[3px] shadow-[0_30px_80px_-20px_rgba(224,26,79,0.5)]">
                <div className="w-full h-full rounded-[42px] overflow-hidden bg-black relative">
                    {SCREENSHOTS.map((s, i) => (
                        <div
                            key={i}
                            className="absolute inset-0 transition-opacity duration-700"
                            style={{ opacity: i === idx ? 1 : 0 }}
                        >
                            <div
                                className="absolute inset-0 bg-cover bg-center"
                                style={{ backgroundImage: `url(${s.url})` }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                            <div className="absolute bottom-0 inset-x-0 p-5 z-10">
                                <div className="text-sm font-heading font-bold">{s.caption}</div>
                                <div className="text-xs text-white/80 mt-1 max-w-[80%]">
                                    {s.subtitle}
                                </div>
                            </div>
                            <div className="absolute right-3 bottom-28 flex flex-col gap-3 items-center z-10">
                                <div className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center">
                                    <HeartIcon size={22} weight="fill" color="#E01A4F" />
                                </div>
                                <span className="text-[10px] font-bold">
                                    {(12 + i * 4).toFixed(1)}K
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="hidden md:flex absolute -left-10 top-12 bg-surface-elevated/80 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3 items-center gap-2">
                <TrendUpIcon size={18} weight="fill" color="#FFA700" />
                <span className="text-xs font-bold">Trending in India</span>
            </div>
            <div className="hidden md:flex absolute -right-8 bottom-20 bg-surface-elevated/80 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3 items-center gap-2">
                <LightningIcon size={18} weight="fill" color="#E01A4F" />
                <span className="text-xs font-bold">Viral in 24h</span>
            </div>
        </div>
    );
}

export default function Home() {
    return (
        <div className="overflow-hidden">
            <Seo path="/" />

            {/* HERO */}
            <section className="relative px-5 md:px-10 max-w-7xl mx-auto pt-12 md:pt-20 pb-20">
                <div className="absolute -top-20 left-1/4 w-[520px] h-[520px] bg-primary/25 rounded-full blur-[140px] pointer-events-none" />
                <div className="absolute top-40 right-0 w-[420px] h-[420px] bg-secondary/15 rounded-full blur-[140px] pointer-events-none" />

                <div className="grid md:grid-cols-[1.15fr_1fr] gap-12 items-center relative">
                    <div className="fade-up">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 mb-6">
                            <SparkleIcon size={14} weight="fill" color="#FFA700" />
                            <span className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">
                                Made in India
                            </span>
                        </div>
                        <h1
                            data-testid="hero-title"
                            className="font-heading font-black text-5xl md:text-7xl leading-[0.95] tracking-tight"
                        >
                            <span className="gradient-text">India&apos;s</span>
                            <br />
                            Own Reels
                            <br />
                            Platform.
                        </h1>
                        <p
                            data-testid="hero-tagline"
                            className="mt-6 text-lg md:text-xl text-white/70 max-w-xl font-body"
                        >
                            Bollywood beats. Cricket nostalgia. Street food. Shayari. Vlogs. Sab kuch
                            ek scroll par — desi flavour ke saath.
                        </p>

                        <div id="download" className="mt-10 flex flex-wrap gap-3 scroll-mt-24">
                            <DownloadBtn
                                store="android"
                                primary
                                testid="hero-android-btn"
                                available
                            />
                            <DownloadBtn store="ios" testid="hero-ios-btn" />
                        </div>

                        <div className="mt-6 flex items-center gap-4 text-sm text-text-secondary flex-wrap">
                            <div className="flex items-center gap-1">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <StarIcon key={i} size={16} weight="fill" color="#FFA700" />
                                ))}
                            </div>
                            <span className="text-white font-bold">4.8</span>
                            <span>·</span>
                            <span>12K+ creators on waitlist</span>
                        </div>
                    </div>

                    <PhonePreview delay={120} />
                </div>

                {/* Stats */}
                <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 relative">
                    {STATS.map((s) => (
                        <div
                            key={s.label}
                            className="rounded-3xl border border-white/10 bg-surface/60 p-5 md:p-6 backdrop-blur-sm hover:border-primary/40 transition-colors"
                            data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <div className="font-heading font-black text-3xl md:text-4xl gradient-text">
                                {s.value}
                            </div>
                            <div className="text-xs uppercase tracking-[0.2em] text-text-secondary mt-2">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Hashtag marquee */}
            <section className="border-y border-white/5 py-6 overflow-hidden">
                <div className="marquee-track">
                    {["bollywood", "cricket", "streetfood", "wedding", "comedy", "fashion", "travel", "fitness", "tech", "dance", "shayari", "vlogs"]
                        .concat(["bollywood", "cricket", "streetfood", "wedding", "comedy", "fashion", "travel", "fitness", "tech", "dance", "shayari", "vlogs"])
                        .map((t, i) => (
                            <span
                                key={i}
                                className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight px-6 whitespace-nowrap"
                            >
                                #{t}
                                <span className="text-secondary mx-3">·</span>
                            </span>
                        ))}
                </div>
            </section>

            {/* APP SCREENSHOTS SHOWCASE */}
            <section className="px-5 md:px-10 max-w-7xl mx-auto py-20 md:py-28" data-testid="screenshots-section">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                        the app
                    </div>
                    <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight">
                        Built for <span className="gradient-text">India&apos;s thumb</span>.
                    </h2>
                    <p className="text-white/70 mt-4">
                        Every pixel obsessed over. Designed for one-handed scrolling, blazing-fast
                        playback and seamless creation.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {SCREENSHOTS.map((s, i) => (
                        <div
                            key={i}
                            data-testid={`screenshot-${i}`}
                            className="relative aspect-[9/16] rounded-3xl overflow-hidden border border-white/10 bg-surface-elevated group hover:scale-[1.02] transition-transform"
                            style={{ animationDelay: `${i * 80}ms` }}
                        >
                            <img
                                src={s.url}
                                alt={s.caption}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                            <div className="absolute bottom-3 inset-x-3 text-left">
                                <div className="font-heading font-bold text-sm">{s.caption}</div>
                                <div className="text-[11px] text-white/80 line-clamp-1 mt-0.5">
                                    {s.subtitle}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURES */}
            <section
                id="features"
                className="px-5 md:px-10 max-w-7xl mx-auto py-20 md:py-28 scroll-mt-24"
                data-testid="features-section"
            >
                <div className="max-w-2xl mb-14">
                    <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                        why india reels
                    </div>
                    <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight">
                        Built for desi creators.
                        <br />
                        <span className="text-white/40">No filter on flavour.</span>
                    </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-5">
                    {FEATURES.map((f) => (
                        <div
                            key={f.title}
                            data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                            className="rounded-3xl border border-white/10 bg-surface p-7 hover:border-white/20 hover:bg-surface-elevated transition-colors"
                        >
                            <div
                                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                                style={{ background: `${f.color}1F` }}
                            >
                                <f.icon size={28} weight="fill" color={f.color} />
                            </div>
                            <h3 className="font-heading font-bold text-xl mb-2">{f.title}</h3>
                            <p className="text-text-secondary text-sm leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-10 text-center">
                    <Link
                        to="/features"
                        data-testid="see-all-features"
                        className="btn-ghost inline-flex items-center gap-2 px-6 py-3"
                    >
                        See all features
                        <ArrowRightIcon size={18} weight="bold" />
                    </Link>
                </div>
            </section>

            {/* CREATOR SECTION */}
            <section className="px-5 md:px-10 max-w-7xl mx-auto py-20 md:py-28" data-testid="creators-section">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                            creator economy
                        </div>
                        <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight">
                            Join 50,000+ <br />
                            <span className="gradient-text">Indian creators</span>.
                        </h2>
                        <p className="text-white/70 mt-5 text-base md:text-lg leading-relaxed">
                            Free to upload. Fair to earn. India Reels gives every creator equal
                            algorithmic chance and monthly Creator Fund payouts via UPI.
                        </p>

                        <div className="mt-6 space-y-3">
                            <Bullet icon={<ShieldCheckIcon size={20} weight="fill" color="#FFA700" />}>
                                Zero algorithm bias against small creators
                            </Bullet>
                            <Bullet icon={<PaintBrushIcon size={20} weight="fill" color="#FFA700" />}>
                                Built-in editor with desi music &amp; effects
                            </Bullet>
                            <Bullet icon={<UsersThreeIcon size={20} weight="fill" color="#FFA700" />}>
                                Direct UPI payouts — no middlemen, no foreign currency
                            </Bullet>
                            <Bullet icon={<MusicNotesIcon size={20} weight="fill" color="#FFA700" />}>
                                Royalty-free Indian music library (T-Series, Saregama coming soon)
                            </Bullet>
                        </div>

                        <Link
                            to="/about"
                            data-testid="creator-cta"
                            className="btn-primary mt-8 inline-flex items-center gap-2 px-6 py-3"
                        >
                            Read our story
                            <ArrowRightIcon size={18} weight="bold" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {CREATORS.map((c, i) => (
                            <div
                                key={c.handle}
                                data-testid={`creator-card-${i}`}
                                className={`rounded-3xl overflow-hidden border border-white/10 bg-surface group ${
                                    i % 2 === 1 ? "mt-8" : ""
                                }`}
                            >
                                <div className="aspect-square overflow-hidden">
                                    <img
                                        src={c.url}
                                        alt={c.handle}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-4">
                                    <div className="font-heading font-bold text-sm">{c.handle}</div>
                                    <div className="flex items-center justify-between mt-1.5 text-xs">
                                        <span className="text-text-secondary">{c.niche}</span>
                                        <span className="text-secondary font-bold">{c.followers}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* DOWNLOAD CTA */}
            <section
                id="get-the-app"
                className="px-5 md:px-10 max-w-7xl mx-auto py-20 md:py-24 scroll-mt-24"
            >
                <div className="rounded-3xl bg-gradient-to-br from-primary/25 via-surface to-secondary/15 border border-white/10 p-8 md:p-16 relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(224,26,79,0.18),transparent_60%)]" />
                    <div className="relative text-center max-w-2xl mx-auto">
                        <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                            ready to scroll?
                        </div>
                        <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight">
                            Download India Reels.
                            <br />
                            <span className="gradient-text">Bharat is watching.</span>
                        </h2>
                        <p className="text-white/70 mt-4 max-w-lg mx-auto">
                            Coming soon to Google Play and Apple App Store. Join the waitlist on
                            launch day to claim your handle.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3 justify-center">
                            <DownloadBtn store="android" primary testid="cta-android-btn" available />
                            <DownloadBtn store="ios" testid="cta-ios-btn" />
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="px-5 md:px-10 max-w-4xl mx-auto py-20 md:py-24" data-testid="faq-section">
                <div className="text-center mb-12">
                    <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                        faq
                    </div>
                    <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight">
                        Frequently asked.
                    </h2>
                    <p className="text-white/70 mt-3">
                        Got more questions?{" "}
                        <Link to="/support" className="text-primary underline-offset-4 hover:underline">
                            Reach our support team
                        </Link>
                        .
                    </p>
                </div>
                <div className="space-y-3">
                    {FAQS.map((f, i) => (
                        <FAQItem key={i} idx={i} q={f.q} a={f.a} />
                    ))}
                </div>
            </section>

            {/* Footer-CTA strip */}
            <section className="px-5 md:px-10 max-w-7xl mx-auto py-10">
                <Link
                    to="/contact"
                    className="rounded-3xl border border-white/10 bg-surface hover:bg-surface-elevated transition-colors p-6 md:p-8 flex items-center justify-between gap-4"
                    data-testid="footer-contact-strip"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
                            <ChatCenteredDotsIcon size={26} weight="duotone" color="#E01A4F" />
                        </div>
                        <div>
                            <div className="font-heading font-bold text-lg">Press, brand or partnership query?</div>
                            <div className="text-text-secondary text-sm">Talk to our team — we reply within 24 hours.</div>
                        </div>
                    </div>
                    <ArrowRightIcon size={22} weight="bold" className="shrink-0 text-primary" />
                </Link>
            </section>
        </div>
    );
}

function Bullet({ icon, children }) {
    return (
        <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-secondary/10 flex items-center justify-center shrink-0 mt-0.5">
                {icon}
            </div>
            <div className="text-white/85 text-sm md:text-base">{children}</div>
        </div>
    );
}

export function DownloadBtn({ store, primary, available, testid }) {
    const isAndroid = store === "android";
    const Icon = isAndroid ? AndroidLogoIcon : AppleLogoIcon;
    const sub = isAndroid ? (available ? "Get it on" : "Coming soon to") : "Coming soon to";
    const main = isAndroid ? "Google Play" : "App Store";

    const handleClick = (e) => {
        e.preventDefault();
        // Open Play Store URL if Android available, else show waitlist behavior
        if (isAndroid && available) {
            // Placeholder Play Store URL — replace with real link on launch
            window.open("https://play.google.com/store/apps?q=india+reels", "_blank");
        } else {
            alert(`${main} launching soon! Stay tuned 🎬`);
        }
    };

    const base =
        "inline-flex items-center gap-3 px-5 py-3.5 rounded-2xl transition-all text-left";
    const cls = primary
        ? `${base} btn-primary`
        : `${base} btn-ghost`;

    return (
        <button onClick={handleClick} data-testid={testid} className={cls}>
            <Icon size={28} weight="fill" />
            <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-wider opacity-80">{sub}</div>
                <div className="font-heading font-bold text-base">{main}</div>
            </div>
            {!available && (
                <span className="ml-1 text-[10px] uppercase tracking-wider bg-secondary/20 text-secondary px-2 py-0.5 rounded-full font-bold">
                    Soon
                </span>
            )}
        </button>
    );
}
